import { DeviceSelectionTable } from './device-selection-table';
import { EvarReportSizingViews } from './evar-report-views';

import { PatientField, ReportPage } from '@/components/report';
import type {
    IAssemblyEntry,
    ICatalogDescriptor,
    IntroductionSide,
} from '@/workflows/vascular/common/catalog';

// ============================================================================
// Types
// ============================================================================

/** Report data initialized from case, editable by the user before export. */
export interface EvarReportData {
    // ── From case data ──
    patientName: string;
    patientId: string;
    dateOfBirth: string;
    gender: string;
    studyDate: string;
    studyDescription: string;
    // ── Report-specific (not from case) ──
    interventionDate: string;
    interventionLocation: string;
}

interface EvarReportPage1Props {
    data: EvarReportData;
    onUpdate: (patch: Partial<EvarReportData>) => void;
    totalPages: number;
    assembly: readonly IAssemblyEntry[];
    catalogDescriptors: readonly ICatalogDescriptor[];
    introductionSide?: IntroductionSide;
    /** Render the sizing views inline; false when they get their own page. */
    showViews: boolean;
}

/**
 * Report page 1: header, patient info, device selection (only when devices
 * were added), and — unless a long device selection pushes them to their own
 * page — the sizing views.
 */
export function EvarReportPage1({
    data,
    onUpdate,
    totalPages,
    assembly,
    catalogDescriptors,
    introductionSide,
    showViews,
}: EvarReportPage1Props) {
    const hasDeviceSelection = assembly.length > 0;

    return (
        <ReportPage pageNumber={1} totalPages={totalPages}>
            <div
                className="absolute top-0 left-0 right-0 h-24 bg-top bg-no-repeat bg-cover z-0"
                style={{ backgroundImage: 'url(/report/header.png)' }}
            />

            <div className="text-center mb-4 z-10">
                <h1 className="text-3xl font-bold text-primary">Aorto-iliac Sizing report</h1>
                <p className="text-sm text-gray-500 mt-1 flex items-center justify-center gap-1">
                    Powered by
                    <img src="/report/logo_endosize.png" alt="EndoSize" className="h-4 inline-block" />
                </p>
            </div>

            <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-gray-300">
                Patient Information
            </h2>
            {/* ── Patient information grid ── */}
            <div className="mb-4 grid grid-cols-2 gap-x-4 gap-y-2">
                <PatientField
                    label="Patient Name"
                    value={data.patientName}
                    onChange={(v) => onUpdate({ patientName: v })}
                />
                <PatientField
                    label="Patient ID"
                    value={data.patientId}
                    onChange={(v) => onUpdate({ patientId: v })}
                />
                <PatientField
                    label="DOB / Age"
                    value={data.dateOfBirth}
                    onChange={(v) => onUpdate({ dateOfBirth: v })}
                />
                <PatientField label="Gender" value={data.gender} onChange={(v) => onUpdate({ gender: v })} />
                <PatientField
                    label="Study Date"
                    value={data.studyDate}
                    onChange={(v) => onUpdate({ studyDate: v })}
                />
                <PatientField
                    label="Study Description"
                    value={data.studyDescription}
                    onChange={(v) => onUpdate({ studyDescription: v })}
                />
                <PatientField
                    label="Intervention Date"
                    value={data.interventionDate}
                    onChange={(v) => onUpdate({ interventionDate: v })}
                    type="date"
                />
                <PatientField
                    label="Intervention Location"
                    value={data.interventionLocation}
                    onChange={(v) => onUpdate({ interventionLocation: v })}
                    placeholder="Enter location..."
                />
            </div>

            {/* ── Strategy: introduction side + device selection ── */}
            {hasDeviceSelection && (
                <div className="mb-4">
                    <h2 className="mb-2 border-b border-gray-300 pb-1 text-sm font-semibold uppercase tracking-wide text-gray-700">
                        Strategy — Device Selection
                    </h2>
                    <div className="flex gap-4">
                        <div className="w-48 shrink-0 border-r border-gray-300 pr-3">
                            <div className="px-1 py-1 text-[11px] font-normal font-mono text-gray-700">
                                Introduction side:{' '}
                                <span className="text-gray-800 font-semibold">
                                    {introductionSide === 'right'
                                        ? 'Right'
                                        : introductionSide === 'left'
                                          ? 'Left'
                                          : '—'}
                                </span>
                            </div>
                        </div>
                        <div className="min-w-0 flex-1">
                            <DeviceSelectionTable
                                assembly={assembly}
                                catalogDescriptors={catalogDescriptors}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* ── Two-column: SVG sizing sheet | 3D view (unless on own page) ── */}
            {showViews && <EvarReportSizingViews />}
        </ReportPage>
    );
}
